import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Search, X, Check, AlertTriangle, Sparkles, Edit3, Filter, RefreshCcw } from "lucide-react";
import Pagination, { usePagination } from "../shared/Pagination";
import { useProductShapes } from "../context/ProductShapeContext";

/* ── helper: normalise API status → boolean ── */
const isActive = (item) => item.status === "Active";

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border animate-slide-up
            ${t.type === "success" ? "bg-[#f0faf4] border-[#bbf7d0] text-[#1a6b3c]"
              : t.type === "warning" ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-red-50 border-red-200 text-red-700"}`}>
          {t.type === "success" && <Check size={15} className="text-[#22A65A] flex-shrink-0" />}
          {t.type === "warning" && <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL
───────────────────────────────────────────── */
function Modal({ open, onClose, children, maxW = "max-w-md" }) {
  const isSidePanel = maxW !== "max-w-sm";
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  if (isSidePanel) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col overflow-hidden">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} border border-gray-100`}>{children}</div>
    </div>
  );
}

function ModalHeader({ title, icon: Icon, onClose }) {
  return (
    <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-white tracking-tight">{title}</h2>
      <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
        <X size={16} className="text-white" />
      </button>
    </div>
  );
}

function ModalFooter({ onCancel, onConfirm, confirmLabel }) {
  return (
    <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
      <button onClick={onCancel}
        className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
        Cancel
      </button>
      <button onClick={onConfirm}
        className="flex-1 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors shadow-sm flex items-center justify-center gap-2">
        {confirmLabel}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SHARED FIELDS
───────────────────────────────────────────── */
function Field({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function TextInput({ error, ...props }) {
  return (
    <input {...props}
      className={`w-full h-10 px-3 rounded-xl border text-sm bg-white text-gray-800 placeholder-gray-400 outline-none transition-all
        ${error ? "border-red-300 ring-2 ring-red-100"
          : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-teal-500 focus:ring-2 focus:ring-teal-100"}`}
    />
  );
}

function StatusRow({ active, onChange }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#e2ddd4] bg-white">
      <div>
        <p className="text-sm font-medium text-gray-700">Status</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {active ? "This item is currently active" : "This item is currently inactive"}
        </p>
      </div>
      <button type="button" onClick={onChange}
        className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200
          ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}>
        <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
          ${active ? "translate-x-5" : "translate-x-1"}`} />
      </button>
    </div>
  );
}

function StatusToggle({ active, onChange }) {
  return (
    <button type="button" onClick={onChange}
      className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200 flex-shrink-0
        ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}>
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
        ${active ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

/* ─────────────────────────────────────────────
   ADD MODAL
───────────────────────────────────────────── */
function AddModal({ open, onClose, onSave, existingShapes = [] }) {
  const [name, setName]     = useState("");
  const [active, setActive] = useState(true);
  const [error, setError]   = useState("");

  useEffect(() => { if (open) { setName(""); setActive(true); setError(""); } }, [open]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) { setError("Shape name is required"); return; }

    const isDuplicate = existingShapes.some(
      s => (s.name || "").trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) { setError("A shape with this name already exists"); return; }

    onSave({ name: trimmed, active });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title="Add Shape" icon={Plus} onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <Field label="Shape Name" required error={error}>
          <TextInput placeholder="e.g., Rectangle, Square, Triangle" value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setError(""); }} error={error} />
        </Field>
        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave}
        confirmLabel={<><Check size={14} /> Save Shape</>} />
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   EDIT MODAL
───────────────────────────────────────────── */
function EditModal({ open, onClose, onSave, item, existingShapes = [] }) {
  const [name, setName]     = useState("");
  const [active, setActive] = useState(true);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (open && item) {
      setName(item.name || "");
      setActive(isActive(item));
      setError("");
    }
  }, [open, item]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) { setError("Name is required"); return; }

    const isDuplicate = existingShapes.some(
      s => s.id !== item.id && (s.name || "").trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) { setError("A shape with this name already exists"); return; }

    onSave(item.id, { name: trimmed, active });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title="Edit Shape" icon={Pencil} onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <Field label="Shape Name" required error={error}>
          <TextInput placeholder="e.g., Round, Oval, Square" value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setError(""); }} error={error} />
        </Field>
        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave}
        confirmLabel={<><Check size={14} /> Update Shape</>} />
    </Modal>
  );
}
/* ─────────────────────────────────────────────
   DELETE MODAL
───────────────────────────────────────────── */
function DeleteModal({ open, onClose, onConfirm, item }) {
  return (
    <Modal open={open} onClose={onClose} maxW="max-w-sm">
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Delete Shape?</h3>
        <p className="text-sm text-gray-500 mb-6">
          Are you sure you want to delete <span className="font-semibold text-gray-700">"{item?.name}"</span>? This cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all active:scale-95">
            Cancel
          </button>
          <button onClick={() => { onConfirm(item?.id); onClose(); }}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all active:scale-95 flex items-center justify-center gap-2">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   SKELETON ROW
───────────────────────────────────────────── */
function SkeletonRow() {
  return (
    <tr className="border-b border-[#f5f0e8]">
      <td className="px-5 py-4"><div className="h-4 w-6 bg-gray-100 rounded animate-pulse" /></td>
      <td className="px-5 py-4"><div className="h-4 w-32 bg-gray-100 rounded animate-pulse" /></td>
      <td className="px-5 py-4">
        <div className="flex items-center justify-end gap-3">
          <div className="h-6 w-11 bg-gray-100 rounded-full animate-pulse" />
          <div className="h-7 w-7 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-7 w-7 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </td>
    </tr>
  );
}

/* ─────────────────────────────────────────────
   SHAPES PAGE
───────────────────────────────────────────── */
export default function ShapesPage() {
  const { shapes, shapeloading, addShape, updateShape, toggleStatus, deleteShape } =
    useProductShapes();

  const [search, setSearch]     = useState("");
  const [sortBy, setSortBy]     = useState("");
  const [addOpen, setAddOpen]   = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem]   = useState(null);
  const { toasts, show: toast } = useToast();

  const activeCount   = shapes.filter(isActive).length;
  const inactiveCount = shapes.length - activeCount;

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  let filtered = shapes.filter((i) => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all"
        ? true
        : statusFilter === "active"
        ? i.status === "Active"
        : i.status === "Inactive";
    return matchSearch && matchStatus;
  });

  if (sortBy === "name_asc")  filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  if (sortBy === "name_desc") filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
  if (sortBy === "active_first")
    filtered = [...filtered].sort((a, b) => (b.status === "Active") - (a.status === "Active"));
  if (sortBy === "inactive_first")
    filtered = [...filtered].sort((a, b) => (a.status === "Active") - (b.status === "Active"));

  const { page: shPage, pageSize: shSize, setPage: setShPage, setPageSize: setShSize, totalPages: shTotalPages, totalItems: shTotal, paginated: paginatedSh } = usePagination(filtered, 10);

  const handleAdd = async ({ name, active }) => {
    await addShape({ name, status: active ? "Active" : "Inactive" });
    toast("Shape added successfully");
  };

  const handleEdit = async (id, { name, active }) => {
    await updateShape(id, { name, status: active ? "Active" : "Inactive" });
    toast("Shape updated successfully");
  };

  const handleDelete = async (id) => {
    await deleteShape(id);
    toast("Shape deleted", "warning");
  };

  const handleToggle = async (item) => {
    await toggleStatus(item.id, item.status);
  };

  return (
    <>
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .animate-modal-in { animation: modalIn 0.18s ease-out both; }
        .animate-slide-up { animation: slideUp 0.2s ease-out both; }
      `}</style>

      <div className="space-y-5">

        {/* ── Stat chips ── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-teal-500 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><Sparkles className="w-4 h-4 text-teal-700" /></div>
            <div><p className="text-xl font-bold text-teal-700">{shapes.length}</p><p className="text-xs text-gray-500">Total Shapes</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-green-500 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><Check className="w-4 h-4 text-green-600" /></div>
            <div><p className="text-xl font-bold text-green-600">{activeCount}</p><p className="text-xs text-gray-500">Active</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-red-400 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-4 h-4 text-red-500" /></div>
            <div><p className="text-xl font-bold text-red-500">{inactiveCount}</p><p className="text-xs text-gray-500">Inactive</p></div>
          </div>
        </div>

        {/* ── Search + Filter ── */}
     
<div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
  {/* Search */}
  <div className="relative flex-1 min-w-[180px]">
    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    <input
      type="text"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search shape…"
      className="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
    />
    {search && (
      <button onClick={() => setSearch("")}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
        <X size={13} />
      </button>
    )}
  </div>

  {/* Status filter */}
  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
    className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[150px]">
    <option value="all">All Status ({shapes.length})</option>
    <option value="active">Active ({activeCount})</option>
    <option value="inactive">Inactive ({inactiveCount})</option>
  </select>

  {/* Sort */}
  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
    className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[130px]">
    <option value="">Default</option>
    <option value="name_asc">Name A → Z</option>
    <option value="name_desc">Name Z → A</option>
    <option value="active_first">Active First</option>
    <option value="inactive_first">Inactive First</option>
  </select>

  {/* Reset */}
  {(search || statusFilter !== "all" || sortBy !== "") && (
    <button
      onClick={() => { setSearch(""); setStatusFilter("all"); setSortBy(""); }}
      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm">
      <RefreshCcw size={13} className="text-gray-500" /> Reset
    </button>
  )}

  {/* Add button */}
  <button onClick={() => setAddOpen(true)}
    className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0 ml-auto">
    <Plus size={15} /><span className="hidden sm:inline">Add Shape</span>
  </button>
</div>

        {/* ── Table card ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm overflow-hidden">
          {!shapeloading && shapes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mb-4">
                <Sparkles size={26} className="text-teal-400" />
              </div>
              <p className="text-sm font-semibold text-gray-600 mb-1">No shapes yet</p>
              <p className="text-xs text-gray-400">Click "+ Add Shape" above to get started</p>
            </div>
          ) : !shapeloading && filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <Search size={22} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No results for "<span className="font-medium">{search}</span>"</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-teal-700 text-white">
                  <tr className="bg-teal-700 text-white">
                    <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider w-16">ID</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider">Shape</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider">Status</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shapeloading
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                    : paginatedSh.map((item, idx) => (
                        <tr key={item.id} className={`transition-colors hover:bg-teal-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                          <td className="px-5 py-4 text-sm text-gray-700">{idx + 1}</td>
                          <td className="px-5 py-4 text-sm font-medium text-gray-800">{item.name}</td>
                          <td className="px-5 py-4">
                            <StatusToggle active={isActive(item)} onChange={() => handleToggle(item)} />
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => setEditItem(item)} className="p-1.5 rounded-lg text-teal-700 hover:bg-teal-50 transition-all active:scale-95"><Edit3 size={14} /></button>
                              <button onClick={() => setDelItem(item)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95"><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
             <div className="px-4 border-t border-gray-100">
  <Pagination currentPage={shPage} totalPages={shTotalPages} totalItems={shTotal}
    pageSize={shSize} onPageChange={setShPage} onPageSizeChange={setShSize} />
</div>
            </div>
          )}

         
        </div>
      </div>

    <AddModal open={addOpen} onClose={() => setAddOpen(false)} onSave={handleAdd} existingShapes={shapes} />
<EditModal open={!!editItem} onClose={() => setEditItem(null)} onSave={handleEdit} item={editItem} existingShapes={shapes} />
      <DeleteModal open={!!delItem} onClose={() => setDelItem(null)} onConfirm={handleDelete} item={delItem} />
      <ToastContainer toasts={toasts} />
    </>
  );
}